import { __awaiter, __generator, __read, __spreadArray, __values } from "tslib";
import { dynamicLoader } from '../runtime/bootstrap/scriptLoader';
import { addLogger, log } from '../runtime/logging/broadcastLogger';
import { LogKind } from '../runtime/logging/log.types';
import { makeMacKustoLogger, unlockLoggingQueue } from '../runtime/logging/macKustoLogger';
import { createUUID } from '../runtime/public/utils/uuid';
import { createTrustedTypesPolicy } from '../runtime/bootstrap/trustedTypesPolicy';
export var hvcTrustedTypePolicy = createTrustedTypesPolicy('@centro/hvc-loader');
var globalWindow = window;
function assert(condition, message) {
    if (!condition) {
        var error = new Error(message);
        log({ type: LogKind.Error, message: message, id: '508371', error: error });
        throw error;
    }
}
// Returns true if both initializers are defined
function getFeatureLoadingState(initializerName) {
    if (globalWindow === undefined) {
        log({ type: LogKind.Info, message: 'window is undefined', id: '512105' });
        return false;
    }
    if (globalWindow.m365_admin_features === undefined) {
        log({ type: LogKind.Info, message: 'window.m365_admin_features is undefined', id: '512108' });
        return false;
    }
    if (globalWindow.m365_admin_features.initializersv5 !== undefined &&
        globalWindow.m365_admin_features.initializersv5[initializerName] !== undefined) {
        return true;
    }
    return false;
}
// Returns true if locDbg[cdnUrl] is defined
function getDbgState(initializerName) {
    var _a, _b;
    return !!((_b = (_a = globalWindow.m365_admin_features) === null || _a === void 0 ? void 0 : _a.locDbg) === null || _b === void 0 ? void 0 : _b[initializerName]);
}
var centroConfigUrl = 'https://cdn.config.centro.core.microsoft/uxversion';
var uxVersionCache = {};
var loggersInitialized = false;
var consumerAdapters = new Map();
var consumerPreConfigs = new Map();
var instanceVersionInfo = undefined;
function normalizeCasingForUxVersionData(mixedCaseUxVersionData) {
    if (mixedCaseUxVersionData !== undefined && mixedCaseUxVersionData.hasOwnProperty('__hostApp_config')) {
        for (var hvcProp in mixedCaseUxVersionData) {
            if (!hvcProp.startsWith('__')) {
                mixedCaseUxVersionData[hvcProp.toLowerCase()] = mixedCaseUxVersionData[hvcProp];
                if (hvcProp.toLowerCase() !== hvcProp) {
                    delete mixedCaseUxVersionData[hvcProp];
                }
            }
        }
    }
    return JSON.stringify(mixedCaseUxVersionData);
}
/**
 * Adds a query parameter to a URL.
 *
 * @param versionUrl - The URL to add the query parameter to.
 * @param param - The name of the query parameter.
 * @param value - The value of the query parameter.
 * @returns The updated URL with the added query parameter.
 */
export function addQueryParam(versionUrl, param, value) {
    var url = new URL(versionUrl);
    url.searchParams.append(param, value);
    return url.toString();
}
/**
 * Get the locale from the bundle url of a Centro HVC.
 *
 * @param cdnUrl - The URL of the bundle from the Centro CDN.
 * @returns The locale key.
 */
function extractLocaleFromBundle(cdnUrl) {
    var match = cdnUrl.match(/\.([a-z]{2,3}(?:-[A-Za-z]+)*)\.bundle\.js/);
    return match ? match[1] : null;
}
/**
 * Constructs the full URL that will be used to call uxversion API.
 * @returns A Promise that resolves to the UX version data as a string.
 */
function getUxVersionUrl(params) {
    var hostName = params.hostName, locale = params.locale, baseUrl = params.baseUrl, localMode = params.localMode, pkgver = params.pkgver, bldver = params.bldver, vectors = params.vectors, useCentroConfig = params.useCentroConfig, subFeatures = params.subFeatures;
    if ([bldver, pkgver, localMode].filter(function (p) { return !!p; }).length > 1) {
        throw new Error("Only one of 'bldver', 'pkgver' or 'localMode' can be set at a time.");
    }
    var queryParam = '';
    if (bldver && !useCentroConfig) {
        queryParam = "?bldVer=" + encodeURIComponent(bldver);
    }
    else if (pkgver) {
        queryParam = "?pkgver=" + encodeURIComponent(pkgver);
    }
    if (localMode) {
        queryParam = "?locdbg=true";
    }
    var versionUrl = "" + (useCentroConfig ? centroConfigUrl : baseUrl + '/admin/api/uxversion') + queryParam;
    versionUrl = addQueryParam(versionUrl, 'locale', locale);
    versionUrl = addQueryParam(versionUrl, useCentroConfig ? 'featureconsumer' : 'hostingapp', hostName);
    if (!!subFeatures) {
        versionUrl = addQueryParam(versionUrl, 'enableSubFeatures', subFeatures.toString());
    }
    if (vectors && useCentroConfig) {
        Object.entries(vectors).forEach(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            versionUrl = addQueryParam(versionUrl, key, value);
        });
    }
    return versionUrl;
}
/**
 * Retrieves the UX version data from the server or cache if already available.
 * @param versionUrl - Url that will be used to call uxversion (use getUxVersionUrl to construct).
 * @returns A Promise that resolves to the UX version data as a string.
 * @throws An error if response is not a 200.
 */
function getUxVersionData(versionUrl) {
    return __awaiter(this, void 0, void 0, function () {
        var resPromise;
        return __generator(this, function (_a) {
            // Check if the versionUrl is already cached
            // If it is, return the cached promise
            // Otherwise, create a new promise and cache it
            if (uxVersionCache[versionUrl]) {
                return [2 /*return*/, uxVersionCache[versionUrl]];
            }
            resPromise = new Promise(function (resolve, reject) {
                fetch(versionUrl)
                    .then(function (response) {
                    var correlationId = response.headers.get('x-ms-correlation-id');
                    if (!response.ok) {
                        var errorMsg = "Request to " + versionUrl + " failed, server responseCode: " + response.status + " body: " + response.statusText + " correlationId: " + correlationId;
                        throw new Error(errorMsg);
                    }
                    return response.json();
                })
                    .then(function (jsonData) {
                    resolve(normalizeCasingForUxVersionData(jsonData));
                })
                    .catch(function (error) {
                    var errorMsg = error.message || "Failed to fetch or parse response: " + error;
                    var err = new Error(errorMsg);
                    reject(err);
                });
            });
            uxVersionCache[versionUrl] = resPromise;
            return [2 /*return*/, resPromise];
        });
    });
}
/**
 * Constructs a map of features to corresponding subfeatures they contain.
 * @param subFeatures - List of subfeatures to check their owning features for.
 * @param uxData - UX version data containing feature information.
 * @returns An object containing features as the key and their corresponding subfeatures.
 */
function getApplicableFeaturesFromUxData(subFeatures, uxData) {
    var e_1, _a, e_2, _b;
    var res = {};
    if (!uxData)
        return res;
    try {
        for (var subFeatures_1 = __values(subFeatures), subFeatures_1_1 = subFeatures_1.next(); !subFeatures_1_1.done; subFeatures_1_1 = subFeatures_1.next()) {
            var currSubFeature = subFeatures_1_1.value;
            try {
                for (var _c = (e_2 = void 0, __values(Object.entries(uxData))), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var _e = __read(_d.value, 2), feature = _e[0], featureData = _e[1];
                    // This is not feature data skip
                    if (typeof featureData !== 'object')
                        continue;
                    if (feature === 'ariaLoggerId')
                        continue;
                    if (feature === 'hostingAppUrls')
                        continue;
                    if (feature === '__hostApp_config')
                        continue;
                    if ('hostingAppUrls' in featureData)
                        continue;
                    if (featureData.subFeatures && featureData.subFeatures.includes(currSubFeature)) {
                        // Check if feature was already added, if it is, then append otherwise create new entry
                        if (feature in res) {
                            res[feature] = __spreadArray(__spreadArray([], __read(res[feature])), [currSubFeature]);
                        }
                        else {
                            res[feature] = [currSubFeature];
                        }
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (subFeatures_1_1 && !subFeatures_1_1.done && (_a = subFeatures_1.return)) _a.call(subFeatures_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    log({
        type: LogKind.Info,
        message: 'hvcloader subFeatures was requested',
        id: '532977',
        data: {
            eventName: 'hvcLoader_applicable_features',
            applicableFeatures: JSON.stringify(res),
        },
    });
    return res;
}
export function preloadFeature(params) {
    return __awaiter(this, void 0, void 0, function () {
        var featureName, hostName, locale, baseUrl, appConfig, targetHtmlElement, localMode, pkgver, bldver, adapters, currUxData, preloadFeatureStartTime, appId, centroBaseUrl, versionInfo, kustoEndpoint, localUxData, versionUrl, uxDataFromAPI, error, lowerCaseFeatureName, error, featureData, error, serverLocale, initializerName, error, preConfig, staticAdditionalObjects, foreignEnvMainVersion, applicableFeatures, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    featureName = params.featureName, hostName = params.hostName, locale = params.locale, baseUrl = params.centroEnvironmentBaseUrl, appConfig = params.appConfig, targetHtmlElement = params.targetHtmlElement, localMode = params.localMode, pkgver = params.pkgver, bldver = params.bldver, adapters = params.adapters;
                    currUxData = undefined;
                    // Note: user might not call us from TypeScript environment, so assert on all non-optional params.
                    assert(!!baseUrl, 'centroEnvironmentBaseUrl is missing');
                    assert(!!appConfig, 'appConfig is missing');
                    preloadFeatureStartTime = performance.now();
                    if (targetHtmlElement) {
                        globalWindow = targetHtmlElement.ownerDocument.defaultView || window;
                    }
                    if (!appConfig.appId) {
                        appConfig.appId = createUUID();
                    }
                    appId = appConfig.appId;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 8, , 9]);
                    centroBaseUrl = baseUrl.substring(baseUrl.length - 1) === '/' ? baseUrl.substring(0, baseUrl.length - 1) : baseUrl;
                    versionInfo = require('../../package.json');
                    instanceVersionInfo = versionInfo.version;
                    kustoEndpoint = centroBaseUrl + "/api/instrument/logclient";
                    if (!loggersInitialized) {
                        // do this first
                        addLogger('hvcLoaderKustoLogger', makeMacKustoLogger(appId, kustoEndpoint, hostName, versionInfo.name + '_' + versionInfo.version));
                        unlockLoggingQueue();
                        if (appConfig.additionalLoggers !== undefined) {
                            Object.keys(appConfig.additionalLoggers).forEach(function (key) {
                                addLogger(key, { logger: appConfig.additionalLoggers[key] });
                            });
                        }
                        loggersInitialized = true;
                    }
                    localUxData = appConfig.localUxVersionData;
                    if (!!!localUxData) return [3 /*break*/, 2];
                    log({
                        type: LogKind.Info,
                        message: 'localUxVersionData is provided thru parameter',
                        id: '508372',
                        data: appConfig.localUxVersionData,
                    });
                    uxVersionCache['LOCAL'] = Promise.resolve(localUxData);
                    currUxData = JSON.parse(localUxData);
                    return [3 /*break*/, 4];
                case 2:
                    if (pkgver && bldver) {
                        throw new Error('cannot specify both pkgver and bldver');
                    }
                    versionUrl = getUxVersionUrl({
                        hostName: hostName,
                        locale: locale,
                        baseUrl: baseUrl,
                        localMode: localMode,
                        pkgver: pkgver,
                        bldver: bldver,
                        vectors: appConfig.vectors,
                        useCentroConfig: appConfig.useCentroConfig,
                        subFeatures: appConfig.subFeatures,
                    });
                    return [4 /*yield*/, getUxVersionData(versionUrl)];
                case 3:
                    uxDataFromAPI = _a.sent();
                    log({
                        type: LogKind.Info,
                        message: 'uxVersion Data is provided thru API',
                        id: '539805',
                        data: uxDataFromAPI,
                    });
                    currUxData = JSON.parse(uxDataFromAPI);
                    _a.label = 4;
                case 4:
                    if (!currUxData) {
                        error = new Error('preloadFeature(): no uxData for this configuration');
                        log({
                            type: LogKind.Error,
                            message: 'preloadFeature(): no uxData for this configuration',
                            id: '508419',
                            error: error,
                        });
                        throw error;
                    }
                    lowerCaseFeatureName = featureName.toLowerCase();
                    // uxData doesn't contain this feature
                    if (!currUxData[lowerCaseFeatureName]) {
                        error = new Error("preloadFeature(): HVC " + lowerCaseFeatureName + " is not enabled for this configuration");
                        log({
                            type: LogKind.Error,
                            message: "preloadFeature(): HVC " + lowerCaseFeatureName + " is not enabled for this configuration",
                            id: '508419',
                            error: error,
                        });
                        throw error;
                    }
                    featureData = currUxData[lowerCaseFeatureName];
                    if (featureData.loaderSpec.type !== 'scriptUrl') {
                        error = new Error("Bundle for HVC " + lowerCaseFeatureName + " is not type scriptUrl");
                        log({
                            type: LogKind.Error,
                            message: "preloadFeature(): Bundle for HVC " + lowerCaseFeatureName + " is not type scriptUrl",
                            id: '540889',
                            error: error,
                        });
                        throw error;
                    }
                    serverLocale = extractLocaleFromBundle(featureData.loaderSpec.content);
                    initializerName = (featureData.version === 'localdevbox' ? 'local_build' : featureData.version) + "." + lowerCaseFeatureName + "." + serverLocale;
                    if (!(getFeatureLoadingState(initializerName) && getDbgState(initializerName) === !!localMode)) return [3 /*break*/, 5];
                    log({
                        type: LogKind.Info,
                        message: "preloadFeature(): initializer " + initializerName + " already loaded previously",
                        id: '508465',
                        data: { hvc: lowerCaseFeatureName, initalizer: initializerName },
                    });
                    return [3 /*break*/, 7];
                case 5: return [4 /*yield*/, dynamicLoader(featureData.loaderSpec, hvcTrustedTypePolicy, globalWindow.document)];
                case 6:
                    _a.sent();
                    if (!getFeatureLoadingState(initializerName)) {
                        error = new Error("preloadFeature(): global is not populated after loading for " + initializerName);
                        log({
                            type: LogKind.Error,
                            message: "preloadFeature(): global is not populated after loading for " + initializerName,
                            id: '539806',
                            data: { hvc: lowerCaseFeatureName, initalizer: initializerName },
                            error: error,
                        });
                        throw error;
                    }
                    if (globalWindow.m365_admin_features.locDbg === undefined) {
                        globalWindow.m365_admin_features.locDbg = {};
                    }
                    globalWindow.m365_admin_features.locDbg[initializerName] = !!localMode;
                    preConfig = {
                        hostName: hostName,
                        locale: locale,
                        appId: appConfig.appId,
                        perfPrefix: appConfig.perfPrefix,
                        additionalLoggers: appConfig.additionalLoggers,
                        dataBoundaryDataSource: appConfig.dataBoundaryDataSource,
                        disableAriaLogging: appConfig.disableAriaLogging,
                        useCentroConfig: appConfig.useCentroConfig,
                        vectors: appConfig.vectors,
                        kustoEndpoint: appConfig.kustoEndpoint,
                    };
                    consumerPreConfigs.set(initializerName, preConfig);
                    _a.label = 7;
                case 7:
                    if (adapters) {
                        // store adapters
                        consumerAdapters.set(initializerName, adapters);
                    }
                    staticAdditionalObjects = void 0;
                    // to get more detailed info of which hvc feature causes trouble, adding this extra log
                    try {
                        staticAdditionalObjects = globalWindow.m365_admin_features.staticAdditionalObjectsv5[initializerName];
                    }
                    catch (error) {
                        foreignEnvMainVersion = globalWindow.m365_admin_features.foreignEnvMainVersion;
                        log({
                            type: LogKind.Error,
                            message: foreignEnvMainVersion
                                ? "using v3 Hvc " + lowerCaseFeatureName + ", error when trying to retrieve staticAdditionalObjects, error: " + error.message
                                : "using v2 Hvc " + lowerCaseFeatureName + ", staticAdditionalObjects is not supported, error: " + error.message,
                            id: '521848',
                            error: error,
                        });
                        throw error;
                    }
                    log({
                        type: LogKind.Info,
                        message: 'preloadFeatures() step has succeeded',
                        id: '513585',
                        data: {
                            hvc: lowerCaseFeatureName,
                            initalizer: initializerName,
                            uxdata: JSON.stringify(currUxData),
                            eventName: 'preloadFeature_step',
                            fromStart: performance.now() - preloadFeatureStartTime,
                            UA: navigator.userAgent,
                        },
                    });
                    applicableFeatures = undefined;
                    if (!!appConfig.subFeatures) {
                        applicableFeatures = getApplicableFeaturesFromUxData(appConfig.subFeatures, currUxData);
                    }
                    return [2 /*return*/, [true, staticAdditionalObjects, applicableFeatures]];
                case 8:
                    error_1 = _a.sent();
                    log({
                        type: LogKind.Error,
                        message: 'error in preloadFeature() step ' + error_1.message,
                        id: '508417',
                        data: { uxdata: JSON.stringify(currUxData) },
                        error: error_1,
                    });
                    throw error_1;
                case 9: return [2 /*return*/];
            }
        });
    });
}
/**
 * Bootstrap the HVC.
 *
 * This will return a function which you could use to bootstrap the HVC, the return result
 * of the function can be used to unmount the HVC
 */
export function bootstrapFeature(params) {
    return __awaiter(this, void 0, void 0, function () {
        var bootStrapFeatureStartTime, featureName, hostName, locale, baseUrl, appConfig, localMode, pkgver, bldver, lowerCaseFeatureName, uxVersionConfig, currUxData, _a, _b, versionUrl, error, uxDataFromAPI, error, error, featureData, error, serverLocale, initializerName, loaderData, bootstrapper, error_2, foreignEnvMainVersion, error_3;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 14, , 15]);
                    bootStrapFeatureStartTime = performance.now();
                    featureName = params.featureName, hostName = params.hostName, locale = params.locale, baseUrl = params.centroEnvironmentBaseUrl, appConfig = params.appConfig, localMode = params.localMode, pkgver = params.pkgver, bldver = params.bldver;
                    lowerCaseFeatureName = featureName.toLowerCase();
                    uxVersionConfig = {
                        hostName: hostName,
                        locale: locale,
                        baseUrl: baseUrl,
                        localMode: localMode,
                        pkgver: pkgver,
                        bldver: bldver,
                        vectors: appConfig === null || appConfig === void 0 ? void 0 : appConfig.vectors,
                        useCentroConfig: appConfig === null || appConfig === void 0 ? void 0 : appConfig.useCentroConfig,
                        subFeatures: appConfig === null || appConfig === void 0 ? void 0 : appConfig.subFeatures,
                    };
                    currUxData = undefined;
                    if (!uxVersionCache['LOCAL']) return [3 /*break*/, 2];
                    _b = (_a = JSON).parse;
                    return [4 /*yield*/, uxVersionCache['LOCAL']];
                case 1:
                    currUxData = _b.apply(_a, [_c.sent()]);
                    return [3 /*break*/, 4];
                case 2:
                    versionUrl = getUxVersionUrl(uxVersionConfig);
                    if (!uxVersionCache[versionUrl]) {
                        error = new Error("bootstrapFeature(): preloadFeature() was not called before bootstraping " + lowerCaseFeatureName + " with this configuration " + JSON.stringify(uxVersionConfig));
                        log({
                            type: LogKind.Error,
                            message: "bootstrapFeature(): preloadFeature() was not called before bootstraping " + lowerCaseFeatureName + " with this configuration " + JSON.stringify(uxVersionConfig),
                            id: '539807',
                            data: { hvc: lowerCaseFeatureName, uxVersionConfig: JSON.stringify(uxVersionConfig) },
                            error: error,
                        });
                        throw error;
                    }
                    return [4 /*yield*/, uxVersionCache[versionUrl]];
                case 3:
                    uxDataFromAPI = _c.sent();
                    currUxData = JSON.parse(uxDataFromAPI);
                    _c.label = 4;
                case 4:
                    if (!currUxData) {
                        error = new Error('bootstrapFeature(): no uxData for this configuration');
                        log({
                            type: LogKind.Error,
                            message: 'bootstrapFeature(): no uxData for this configuration',
                            id: '539808',
                            error: error,
                        });
                        throw error;
                    }
                    // uxData doesn't contain this feature
                    if (!currUxData[lowerCaseFeatureName]) {
                        error = new Error("bootstrapFeature(): HVC " + lowerCaseFeatureName + " is not enabled for this configuration");
                        log({
                            type: LogKind.Error,
                            message: "bootstrapFeature(): HVC " + lowerCaseFeatureName + " is not enabled for this configuration",
                            id: '539809',
                            error: error,
                        });
                        throw error;
                    }
                    featureData = currUxData[lowerCaseFeatureName];
                    if (featureData.loaderSpec.type !== 'scriptUrl') {
                        error = new Error("Bundle for HVC " + lowerCaseFeatureName + " is not type scriptUrl");
                        log({
                            type: LogKind.Error,
                            message: "bootstrapFeature(): Bundle for HVC " + lowerCaseFeatureName + " is not type scriptUrl",
                            id: '540888',
                            error: error,
                        });
                        throw error;
                    }
                    serverLocale = extractLocaleFromBundle(featureData.loaderSpec.content);
                    initializerName = (featureData.version === 'localdevbox' ? 'local_build' : featureData.version) + "." + lowerCaseFeatureName + "." + serverLocale;
                    if (!getFeatureLoadingState(initializerName)) return [3 /*break*/, 12];
                    loaderData = {
                        centroBaseUrl: baseUrl,
                        cdnUrl: featureData.cdnUrl,
                        ariaLoggerId: currUxData.__hostApp_config.ariaLoggerId,
                        targetAppUrls: currUxData.__hostApp_config.hostingAppUrls,
                        versionInfo: instanceVersionInfo,
                    };
                    bootstrapper = void 0;
                    _c.label = 5;
                case 5:
                    _c.trys.push([5, 10, , 11]);
                    if (!globalWindow.m365_admin_features.initializersv5[initializerName]) return [3 /*break*/, 7];
                    return [4 /*yield*/, globalWindow.m365_admin_features.initializersv5[initializerName](consumerAdapters.get(initializerName), loaderData, consumerPreConfigs.get(initializerName))];
                case 6:
                    bootstrapper = _c.sent();
                    return [3 /*break*/, 9];
                case 7: return [4 /*yield*/, globalWindow.m365_admin_features.initializers[lowerCaseFeatureName](consumerAdapters.get(initializerName), loaderData, consumerPreConfigs.get(initializerName))];
                case 8:
                    bootstrapper = _c.sent();
                    _c.label = 9;
                case 9:
                    log({
                        type: LogKind.Info,
                        message: 'bootstrapFeature() step has succeeded',
                        id: '513586',
                        data: {
                            hvc: lowerCaseFeatureName,
                            initializer: initializerName,
                            eventName: 'bootstrapFeature_step',
                            fromStart: performance.now() - bootStrapFeatureStartTime,
                            UA: navigator.userAgent,
                        },
                    });
                    return [3 /*break*/, 11];
                case 10:
                    error_2 = _c.sent();
                    foreignEnvMainVersion = globalWindow.m365_admin_features.foreignEnvMainVersion;
                    log({
                        type: LogKind.Error,
                        message: foreignEnvMainVersion
                            ? "using v3 Hvc " + lowerCaseFeatureName + ", error when running foreignFeatureInitializeRuntime, error: " + error_2.message
                            : "using v2 Hvc " + lowerCaseFeatureName + " is not supported, error: " + error_2.message,
                        id: '521851',
                        error: error_2,
                    });
                    throw error_2;
                case 11: return [2 /*return*/, bootstrapper];
                case 12: throw new Error("bootstrapFeature(): preloadFeature() was not called before bootstraping " + lowerCaseFeatureName + " with this configuration " + JSON.stringify(uxVersionConfig));
                case 13: return [3 /*break*/, 15];
                case 14:
                    error_3 = _c.sent();
                    log({
                        type: LogKind.Error,
                        message: 'error in bootstrapFeature() step ' + error_3.message,
                        id: '508481',
                        error: error_3,
                    });
                    throw error_3;
                case 15: return [2 /*return*/];
            }
        });
    });
}
